import React, { useEffect, useState } from 'react';
import OfficialReportLayout, {
  OfficialField,
  OfficialSectionTitle,
} from '../components/print/OfficialReportLayout';
import ReportEmendasFilters, {
  Filters,
  EMPTY_FILTERS,
} from '../components/reports/ReportEmendasFilters';
import ReportPorInstituicaoTable from '../components/reports/ReportPorInstituicaoTable';
import ReportRelacaoTable from '../components/reports/ReportRelacaoTable';
import ReportResumoTable from '../components/reports/ReportResumoTable';
import councilorService, { CouncilorDTO } from '../services/councilorService';
import reportService, {
  RelatorioEmendaInstituicaoItem,
  RelatorioEmendaItem,
  RelatorioEmendaSecretariaStatusItem,
} from '../services/reportService';
import secretariaMunicipalService, { SecretariaMunicipalDTO } from '../services/secretariaMunicipalService';
import tipoEmendaService, { TipoEmendaDTO } from '../services/tipoEmendaService';
import { fMoney } from '../utils/emendaFormatters';
import { UI_THEME } from '../ui/theme';

const isDevolvida = (status?: string) => /devolvid/i.test(status ?? '');

const formatDate = (value?: string) => {
  if (!value) return 'Não informado';
  return new Intl.DateTimeFormat('pt-BR').format(new Date(`${value}T00:00:00`));
};

type ActiveTab = 'por-instituicao' | 'relacao' | 'resumo';

type TabConfig = {
  key: ActiveTab;
  label: string;
  description: string;
  icon: React.ReactNode;
};

const TAB_CONFIG: TabConfig[] = [
  {
    key: 'por-instituicao',
    label: 'Por instituição',
    description: 'Emendas agrupadas por instituição com dotação, parlamentar, valor, código e total.',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  {
    key: 'relacao',
    label: 'Relação de emendas',
    description: 'Lista detalhada com código, parlamentar, instituição, secretaria, etapa e valor.',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
  {
    key: 'resumo',
    label: 'Resumo por secretaria',
    description: 'Totais consolidados agrupados por secretaria e etapa do ciclo de vida.',
    icon: (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
];

const ReportsEmendasPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);

  const [items, setItems] = useState<RelatorioEmendaItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [resumo, setResumo] = useState<RelatorioEmendaSecretariaStatusItem[]>([]);
  const [porInstituicao, setPorInstituicao] = useState<RelatorioEmendaInstituicaoItem[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);

  const [tipos, setTipos] = useState<TipoEmendaDTO[]>([]);
  const [councilors, setCouncilors] = useState<CouncilorDTO[]>([]);
  const [secretarias, setSecretarias] = useState<SecretariaMunicipalDTO[]>([]);

  const isSaas = UI_THEME === 'saas';
  const activeTabClass = isSaas
    ? 'border-teal-600 bg-teal-600 text-white'
    : 'border-blue-700 bg-blue-700 text-white dark:border-blue-600 dark:bg-blue-600';
  const inactiveTabClass =
    'border-slate-200 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  useEffect(() => {
    let active = true;
    Promise.all([
      tipoEmendaService.list().catch(() => []),
      councilorService.list().catch(() => []),
      secretariaMunicipalService.list().catch(() => []),
    ]).then(([t, c, s]) => {
      if (!active) return;
      setTipos(Array.isArray(t) ? t : []);
      setCouncilors(Array.isArray(c) ? c : []);
      setSecretarias(Array.isArray(s) ? s : []);
    });
    return () => { active = false; };
  }, []);

  const handleSelectTab = (tab: ActiveTab) => {
    setActiveTab(tab);
    setHasSearched(false);
    setItems([]);
    setResumo([]);
    setPorInstituicao([]);
    setTotalItems(0);
    setError('');
    setPage(1);
  };

  const fetchData = async (currentPage: number, currentSize = size) => {
    if (!activeTab) return;
    setLoading(true);
    setError('');
    try {
      const payload = { ...filters, page: currentPage - 1, size: currentSize, sort: filters.sort };
      if (activeTab === 'por-instituicao') {
        const data = await reportService.getEmendasPorInstituicao(payload);
        setPorInstituicao(Array.isArray(data) ? data : []);
      } else if (activeTab === 'relacao') {
        const data = await reportService.getEmendas(payload);
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotalItems(data.totalItems || 0);
      } else if (activeTab === 'resumo') {
        const data = await reportService.getResumoEmendasSecretariaStatus(payload);
        setResumo(Array.isArray(data) ? data : []);
      }
      setHasSearched(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Não foi possível carregar o relatório.');
    } finally {
      setLoading(false);
    }
  };

  const handleVisualizar = () => {
    setPage(1);
    fetchData(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchData(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setSize(newSize);
    setPage(1);
    fetchData(1, newSize);
  };

  const handleExport = async (type: 'xlsx' | 'pdf') => {
    try {
      setExporting(type);
      const payload = { ...filters, sort: filters.sort };
      if (type === 'xlsx') await reportService.exportEmendasXlsx(payload);
      else await reportService.exportEmendasPdf(payload);
    } catch (err: any) {
      setError(err?.response?.data?.error || `Não foi possível exportar o arquivo ${type.toUpperCase()}.`);
    } finally {
      setExporting(null);
    }
  };

  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    setHasSearched(false);
    setItems([]);
    setResumo([]);
    setPorInstituicao([]);
    setTotalItems(0);
    setError('');
  };

  const totalGeral = porInstituicao.reduce((sum, g) => sum + (g.total ?? 0), 0);

  const selectedTipo = tipos.find(t => t.codigo === filters.classificacao);
  const selectedCouncilor = councilors.find(c => c.councilorId === filters.councilorId);

  const summaryFields = activeTab === 'por-instituicao'
    ? [
        <OfficialField key="inst" label="Instituições" value={String(porInstituicao.length)} />,
        <OfficialField key="total" label="Valor total" value={fMoney(totalGeral)} />,
      ]
    : activeTab === 'relacao'
    ? [
        <OfficialField key="total" label="Registros encontrados" value={String(totalItems)} />,
        <OfficialField key="pagina" label="Nesta página" value={String(items.length)} />,
      ]
    : activeTab === 'resumo'
    ? [
        <OfficialField key="sec" label="Secretarias" value={String(new Set(resumo.map(r => r.secretariaDestino || '')).size)} />,
        <OfficialField key="etapas" label="Etapas" value={String(new Set(resumo.map(r => r.statusCicloVida || '')).size)} />,
      ]
    : [];

  return (
    <OfficialReportLayout
      moduleLabel="Relatórios de emendas"
      title="Relatório de emendas"
      subtitle={isSaas
        ? 'Escolha uma visão, configure os filtros e gere o relatório operacional das emendas.'
        : 'Selecione o tipo de relatório, configure os filtros e clique em Visualizar para gerar a consulta oficial.'}
      summaryFields={summaryFields}
      toolbar={<></>}
    >
      <OfficialSectionTitle number="1" title="Tipo de relatório" aside="Selecione o formato desejado." />
      <div className="grid gap-3 sm:grid-cols-3">
        {TAB_CONFIG.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleSelectTab(tab.key)}
            className={`flex flex-col items-start gap-2 rounded-xl border-2 p-4 text-left transition-colors ${
              activeTab === tab.key ? activeTabClass : inactiveTabClass
            }`}
          >
            <div className={activeTab === tab.key ? 'text-white' : 'text-slate-500 dark:text-slate-400'}>
              {tab.icon}
            </div>
            <div>
              <p className="text-sm font-semibold">{tab.label}</p>
              <p className={`mt-0.5 text-xs leading-relaxed ${activeTab === tab.key ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
                {tab.description}
              </p>
            </div>
          </button>
        ))}
      </div>

      {activeTab && (
        <>
          <OfficialSectionTitle number="2" title="Filtros de consulta" aside="Preencha os campos e clique em Visualizar." />
          <ReportEmendasFilters
            filters={filters}
            onChange={setFilters}
            activeTab={activeTab}
            tipos={tipos}
            councilors={councilors}
            secretarias={secretarias}
            loading={loading}
            hasSearched={hasSearched}
            exporting={exporting}
            onVisualizar={handleVisualizar}
            onExportXLSX={() => handleExport('xlsx')}
            onExportPDF={() => handleExport('pdf')}
            onClear={handleClear}
          />

          {hasSearched && (
            <>
              <OfficialSectionTitle number="3" title="Parâmetros aplicados" />
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                <OfficialField label="Exercício" value={filters.exercicio || 'Todos'} />
                <OfficialField label="Etapa" value={filters.status || 'Todos'} />
                <OfficialField label="Secretaria" value={filters.secretariaDestino || 'Todas'} />
                <OfficialField label="Tipo" value={selectedTipo?.nome || filters.classificacao || 'Todos'} />
                <OfficialField label="Esfera" value={filters.esfera || 'Todas'} />
                <OfficialField label="Execução" value={filters.tipoTransferencia || 'Todas'} />
                <OfficialField label="Parlamentar" value={selectedCouncilor?.fullName || (filters.councilorId ? filters.councilorId : 'Todos')} />
                <OfficialField label="Data inicial" value={filters.dataInicio ? formatDate(filters.dataInicio) : 'Todos'} />
                <OfficialField label="Data final" value={filters.dataFim ? formatDate(filters.dataFim) : 'Todos'} />
              </div>
            </>
          )}

          <OfficialSectionTitle number={hasSearched ? '4' : '3'} title="Resultado" />

          {hasSearched && !loading && !error && (() => {
            const temDevolvida =
              (activeTab === 'por-instituicao' && porInstituicao.some(g => g.linhas.some(l => isDevolvida(l.statusCicloVida)))) ||
              (activeTab === 'relacao' && items.some(i => isDevolvida(i.statusCicloVida)));
            return temDevolvida ? (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200 print:border-red-300">
                <span className="inline-block h-3 w-3 rounded-sm border border-red-400 bg-danger-soft" />
                Emenda devolvida em alguma fase do processo
              </div>
            ) : null;
          })()}

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">{error}</div>
          )}

          {loading && (
            <div className="rounded-xl border border-slate-200 px-4 py-12 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              Gerando relatório...
            </div>
          )}

          {!loading && !hasSearched && !error && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-12 text-center dark:border-slate-700 dark:bg-slate-900/70">
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                Configure os filtros acima e clique em <strong>Visualizar</strong> para gerar o relatório.
              </p>
            </div>
          )}

          {!loading && hasSearched && !error && (
            <>
              {activeTab === 'por-instituicao' && (
                <ReportPorInstituicaoTable data={porInstituicao} totalGeral={totalGeral} />
              )}

              {activeTab === 'relacao' && (
                <ReportRelacaoTable
                  items={items}
                  totalItems={totalItems}
                  page={page}
                  size={size}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  tipos={tipos}
                />
              )}

              {activeTab === 'resumo' && (
                <ReportResumoTable data={resumo} />
              )}
            </>
          )}
        </>
      )}

      {!activeTab && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-14 text-center dark:border-slate-700 dark:bg-slate-900/70">
          <p className="text-sm text-slate-500 dark:text-slate-400">Selecione um tipo de relatório acima para começar.</p>
        </div>
      )}
    </OfficialReportLayout>
  );
};

export default ReportsEmendasPage;
